import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Tooltip,
  useTheme,
  useMediaQuery,
  Collapse,
  IconButton,
  Badge,
  Button
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import { ZoneCategoryFilter } from '../../models/ZoneCategory';
import { UserRole } from '../../models/User';

interface ZoneFilterSidebarProps {
  selectedCategory: number | null;
  onCategoryChange: (categoryId: number | null) => void;
  totalZones: number;
  filteredZones: number;
  zones: any[]; // Array of zones to count per category
  categories: ZoneCategoryFilter[]; // Dynamic categories from API
  userRole?: UserRole;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  onManageCategories?: () => void; // Callback for category management
}

export const ZoneFilterSidebar: React.FC<ZoneFilterSidebarProps> = ({
  selectedCategory,
  onCategoryChange,
  totalZones,
  filteredZones,
  zones,
  categories,
  userRole,
  isCollapsed = false,
  onToggleCollapse,
  onManageCategories
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Count zones for each category
  const getZoneCountForCategory = (categoryId: number): number => {
    return zones.filter(zone => zone.categoryId === categoryId).length;
  };

  // Get total active zones count
  const getTotalActiveZones = (): number => {
    return zones.length;
  };

  // Check if user can manage categories
  const canManageCategories = userRole === UserRole.HotelManager;

  const FilterChip = ({ 
    categoryId, 
    category, 
    isSelected, 
    count 
  }: { 
    categoryId: number | null, 
    category: ZoneCategoryFilter | null, 
    isSelected: boolean, 
    count: number 
  }) => (
    <Tooltip title={category?.description || "Show all zones"} arrow>
      <Badge 
        badgeContent={count} 
        color="primary"
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          '& .MuiBadge-badge': {
            fontSize: '0.7rem',
            height: 18,
            minWidth: 18,
            backgroundColor: isSelected ? 'white' : 'primary.main',
            color: isSelected ? (category?.color || 'primary.main') : 'white'
          }
        }}
      >
        <Chip
          label={category?.name || "All"}
          onClick={() => onCategoryChange(categoryId)}
          variant={isSelected ? 'filled' : 'outlined'}
          icon={category ? <i className={category.icon} style={{ fontSize: '14px' }} /> : <FilterListIcon fontSize="small" />}
          sx={{
            backgroundColor: isSelected ? (category?.color || 'primary.main') : 'transparent',
            color: isSelected ? 'white' : (category?.color || 'primary.main'),
            borderColor: category?.color || 'primary.main',
            '&:hover': {
              backgroundColor: isSelected 
                ? (category?.color || 'primary.main') 
                : `${category?.color || theme.palette.primary.main}15`,
            },
            '& .MuiChip-label': {
              fontWeight: isSelected ? 600 : 500,
              fontSize: '0.85rem'
            },
            '& .MuiChip-icon': {
              color: isSelected ? 'white' : (category?.color || 'primary.main')
            }
          }}
        />
      </Badge>
    </Tooltip>
  );

  return (
    <Paper 
      elevation={2}
      sx={{ 
        mb: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2
      }}
    >
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        p: 2,
        bgcolor: 'rgba(25, 118, 210, 0.04)',
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterListIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
            Zone Kategorileri
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ({filteredZones}/{totalZones})
          </Typography>
          
          {/* Category Management Button */}
          {canManageCategories && onManageCategories && (
            <Button
              size="small"
              startIcon={<SettingsIcon sx={{ fontSize: 14 }} />}
              onClick={onManageCategories}
              sx={{
                ml: 'auto', minWidth: 'auto',
                textTransform: 'none', fontWeight: 600, fontSize: '0.75rem',
                borderRadius: '8px', px: 1.5, py: 0.4,
                color: '#4294e3',
                backgroundColor: 'rgba(66, 148, 227, 0.08)',
                '&:hover': { backgroundColor: 'rgba(66, 148, 227, 0.15)' }
              }}
            >
              Yönet
            </Button>
          )}
        </Box>
        
        {isMobile && onToggleCollapse && (
          <IconButton 
            onClick={onToggleCollapse}
            size="small"
            sx={{ color: 'primary.main' }}
          >
            {isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          </IconButton>
        )}
      </Box>

      {/* Filter Chips */}
      <Collapse in={!isCollapsed} timeout="auto">
        <Box sx={{ p: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 1.5,
            alignItems: 'center'
          }}>
            {/* All Zones */}
            <FilterChip
              categoryId={null}
              category={null}
              isSelected={selectedCategory === null}
              count={getTotalActiveZones()}
            />
            
            {/* Category Filters */}
            {categories.map((category) => {
              const count = getZoneCountForCategory(category.id);
              return (
                <FilterChip
                  key={category.id}
                  categoryId={category.id}
                  category={category}
                  isSelected={selectedCategory === category.id}
                  count={count}
                />
              );
            })}
          </Box>
          
          {/* Active Filter Info */}
          {selectedCategory && (
            <Box sx={{ 
              mt: 2, 
              p: 1.5, 
              bgcolor: 'rgba(25, 118, 210, 0.08)',
              borderRadius: 1,
              border: '1px solid rgba(25, 118, 210, 0.2)'
            }}>
              {(() => {
                const selectedCategoryData = categories.find(cat => cat.id === selectedCategory);
                return selectedCategoryData ? (
                  <>
                    <Typography variant="body2" color="primary.main" sx={{ fontWeight: 500 }}>
                      <i className={selectedCategoryData.icon} style={{ marginRight: '8px' }} />
                      {selectedCategoryData.name} kategorisi aktif
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedCategoryData.description}
                    </Typography>
                  </>
                ) : null;
              })()}
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};